import { lazy, Suspense } from "react";
import { array, bool, func, object, shape, string } from "prop-types";
import styled, { css, ThemeProvider } from "styled-components/macro";
import { rem } from "polished";

import { colors, fonts } from "../../../styles/Styles.variables";

import PageDesktopMenu from "./Page.desktop.menu";
import PageLogo from "./Page.logo";
import PageSkip from "./Page.skip";

const PageMetaNavAnon = lazy(() => import("./Page.desktop.metanav.anon"));
const PageMetaNavAuth = lazy(() => import("./Page.desktop.metanav.auth"));

export default function PageDesktopHeader({
  authenticated = false,
  location,
  logout,
  settingsLinks,
  skip,
  trackOutboundLink,
  user,
  ...rest
}) {
  return (
    <ThemeProvider theme={authenticated ? themeAuth : themeAnon}>
      <Header>
        <PageSkip onClick={skip} />
        <Suspense fallback={null}>
          {authenticated ? (
            <MetaNav as={PageMetaNavAuth} logout={logout} user={user} />
          ) : (
            <MetaNav
              as={PageMetaNavAnon}
              trackOutboundLink={trackOutboundLink}
            />
          )}
        </Suspense>
        <Nav>
          <Brand>
            <Logo />
            {authenticated && <AuthIndicator>Internal-Only View</AuthIndicator>}
          </Brand>
          <Menu
            authenticated={authenticated}
            location={location}
            settingsLinks={settingsLinks}
          />
        </Nav>
      </Header>
    </ThemeProvider>
  );
}

const themeBase = {
  breakpoints: {
    md: rem("900px"),
    lg: rem("1200px"),
  },
  spacing: {
    x: {
      sm: 6,
      md: 10,
      lg: 18,
    },
  },
};

const themeAuth = {
  ...themeBase,
  header: {
    bgColor: colors.yellow,
    bdColor: colors.red500,
  },
  logoOverride: css`
    margin-top: -${rem("12px")};

    @media (min-width: ${rem("900px")}) {
      margin-top: -${rem("6px")};
    }

    &:focus {
      outline-color: ${colors.black};
    }

    path {
      fill: ${colors.black} !important;
    }
  `,
};

const themeAnon = {
  ...themeBase,
  header: {
    bgColor: colors.black,
    bdColor: colors.yellow,
  },
};

const Header = styled.header`
  background-color: ${({ theme }) => theme.header.bgColor};
  border-bottom: ${rem("8px")} solid ${({ theme }) => theme.header.bdColor};
`;

const MetaNav = styled.div`
  padding-left: ${({ theme }) => rem(30 + theme.spacing.x.sm + "px")};
  padding-right: ${({ theme }) => rem(30 + theme.spacing.x.sm + "px")};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => rem(30 + theme.spacing.x.md + "px")};
    padding-right: ${({ theme }) => rem(30 + theme.spacing.x.md + "px")};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: ${({ theme }) => rem(40 + theme.spacing.x.lg + "px")};
    padding-right: ${({ theme }) => rem(40 + theme.spacing.x.lg + "px")};
  }
`;

const Nav = styled.nav`
  align-items: center;
  display: flex;
  height: ${rem("62px")};
  padding: 0 ${rem("30px")} ${rem("12px")};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: ${rem("78px")};
    font-size: ${rem("40px")};
    padding-left: ${rem("42px")};
    padding-right: ${rem("42px")};
  }
`;

const Brand = styled.div`
  max-width: 100%;
  padding: ${rem("20px")} ${({ theme }) => rem(theme.spacing.x.sm + "px")};
  position: relative;
  width: ${({ theme }) => rem(150 + theme.spacing.x.sm * 2 + "px")};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => rem(theme.spacing.x.md + "px")};
    padding-right: ${({ theme }) => rem(theme.spacing.x.md + "px")};
    width: ${({ theme }) => rem(215 + theme.spacing.x.md * 2 + "px")};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: ${({ theme }) => rem(theme.spacing.x.lg + "px")};
    padding-right: ${({ theme }) => rem(theme.spacing.x.lg + "px")};
    width: ${({ theme }) => rem(275 + theme.spacing.x.lg * 2 + "px")};
  }
`;

const Logo = styled(PageLogo)`
  height: 0;
  overflow: hidden;
  max-width: 100%;
  padding-top: 11.336032389%;
  position: relative;

  svg {
    bottom: 0;
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
  }

  ${({ theme }) => theme.logoOverride || null}
`;

const AuthIndicator = styled.div`
  background-color: ${colors.red500};
  color: ${colors.white};
  font-size: 31%;
  font-weight: ${fonts.weights.bold};
  line-height: 150%;
  margin-top: calc(11.336032389% + ${rem("8px")});
  padding: 0 ${rem("5px")};
  position: absolute;
  text-transform: uppercase;
  top: 0;
  white-space: nowrap;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: calc(11.336032389% + ${rem("14px")});
  }
`;

const Menu = styled(PageDesktopMenu)`
  padding: 0 0 0 ${({ theme }) => rem(theme.spacing.x.sm + "px")};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => rem(theme.spacing.x.md + "px")};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: ${({ theme }) => rem(theme.spacing.x.lg + "px")};
  }
`;

PageDesktopHeader.propTypes = {
  authenticated: bool,
  location: object,
  logout: func.isRequired,
  settingsLinks: array,
  skip: func,
  trackOutboundLink: func,
  user: shape({
    first_name: string,
    last_name: string,
  }),
};
